import { TeamMemberType } from "@/interfaces";
import { DEFAULT_CACHE_TIME } from "@/utils/constants";
import { useQuery } from "@tanstack/react-query";
import api from "./api";

export interface TeamMembersListResponse {
  data: TeamMemberType[];
  total: number;
  page?: number;
  limit?: number;
}

/**
 * React Query hook to fetch team members by client ID
 * Transforms backend ClientTeamMember[] to TeamMemberType[]
 */
export const useGetTeamMembersByClientId = (clientId: string, companyId: string) => {
  return useQuery<TeamMembersListResponse>({
    queryKey: ["team-members", clientId, companyId],
    queryFn: async () => {
      const params = companyId ? `?companyId=${companyId}` : "";
      const response = await api.get(`/clients/${clientId}/teams${params}`);

      // Transform backend ClientTeamMember entities to frontend TeamMemberType
      const transformedData = (response.data?.data || []).map((item: any) => {
        const user = item.teamMember || {};
        return {
          id: user.id || item.userId,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.phone || "",
          role: item.role || user.role || "",
          department: user.designation || "",
          designation: user.designation || "",
          status: user.isActive ? "Active" : "Inactive",
          isActive: user.isActive || false,
          joinedDate: item.createdAt || user.createdAt || "",
          lastActive: user.lastLoginTimestamp || user.updatedAt || "",
        };
      });

      return {
        data: transformedData,
        total: response.data?.total || transformedData.length,
        page: response.data?.page,
        limit: response.data?.limit,
      };
    },
    staleTime: DEFAULT_CACHE_TIME,
    enabled: !!clientId && !!companyId,
  });
};

/**
 * Interface for raw ClientTeamMember data from backend
 */
export interface ClientTeamMemberType {
  id: string;
  clientId: string;
  userId: string;
  companyId: string;
  role: string;
  createdAt: string;
  updatedAt: string;
  teamMember: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    designation?: string;
    isActive: boolean;
  };
}

export interface ClientTeamMembersResponse {
  data: ClientTeamMemberType[];
  total: number;
  page?: number;
  limit?: number;
}

/**
 * React Query hook to fetch raw ClientTeamMember data by client ID
 * Returns raw backend ClientTeamMember entities with clientTeamMemberId intact
 * Use this for commission creation where clientTeamMemberId is required
 */
export const useGetClientTeamMembers = (clientId: string, companyId: string) => {
  return useQuery<ClientTeamMembersResponse>({
    queryKey: ["client-team-members", clientId, companyId],
    queryFn: async () => {
      const params = companyId ? `?companyId=${companyId}` : "";
      const response = await api.get(`/clients/${clientId}/teams${params}`);

      return {
        data: response.data?.data || [],
        total: response.data?.total || 0,
        page: response.data?.page,
        limit: response.data?.limit,
      };
    },
    staleTime: DEFAULT_CACHE_TIME,
    enabled: !!clientId && !!companyId,
  });
};
